import { useEffect, useMemo, useRef, useState } from 'react';
import {
  RunnerSet,
  type Hit,
  type Suite,
  type SuiteEvent,
  type SuiteSnapshot,
} from '@checkpoint-core';
import { useLogcatChunks } from '@/lib/logcat/useLogcatStream';
import { convertLogLine } from './convertLogLine';
import type { LogLine } from '@/lib/logcat/parser';

export interface MountedSuite {
  /** 调度时分配的 runId（前端 React key）。 */
  runId: string;
  /** 套件（已从后端 fetch 完整对象）。 */
  suite: Suite;
  /** 用户传入的参数原始值。 */
  params: Record<string, string | number | boolean>;
  /** 是否处于 active；inactive 时 RunnerSet.pause(runId) 暂停 feed。 */
  active: boolean;
}

export interface RunnerSetController {
  snapshots: SuiteSnapshot[];
  /** lineId -> 命中数组（含跨套件）。 */
  hitsAtFn: (lineId: number) => Hit[];
  /** 当前 RunnerSet 已经分配到的 lineId 上界。 */
  nextLineId: number;
  /** 收尾全部 runner（"停止全部"按钮）。 */
  finishAll: () => void;
  /** 直接拿到 RunnerSet 实例。 */
  runnerSet: RunnerSet;
  /** 各 run 的命中明细（供导出 / 持久化用）。 */
  hitsByRun: Map<string, Hit[]>;
}

/**
 * useTabRunnerSet —— 每个 logcat tab 持有一个 RunnerSet。
 *
 * 行为约定：
 *   - `mounted` 中 `active=false` 的套件用 `RunnerSet.pause(runId)` 暂停 feed；
 *   - serial 变化时整个 RunnerSet 重置（跨设备 lineId 不可比较）；
 *   - 共享 logcat 流被 `clear()` 时同步重置 nextLineId 与命中索引；
 *   - **新挂载 / suite 引用变化（YAML 编辑后）** 时把 `historyLines` 中
 *     已稳定编号的旧行回放给目标 runner，让选区→规则、热更套件能"追溯
 *     已经看到过的日志"立即得到 verdict，避免出现"明明日志里有但 verdict
 *     还停在等待中"的诡异体验。
 */
export function useTabRunnerSet(opts: {
  serial: string | undefined;
  mounted: MountedSuite[];
  nowMs?: () => number;
  /** 当前可见 logcat 行缓冲；新 runner 加入时会回放这部分历史。 */
  historyLines?: LogLine[];
}): RunnerSetController {
  const { serial, mounted, nowMs, historyLines } = opts;
  const runnerSetRef = useRef<RunnerSet>();
  if (!runnerSetRef.current) runnerSetRef.current = new RunnerSet();
  /** 记录每个 runId 当前用的 suite 引用，用于检测 YAML 编辑后引用变化。 */
  const suiteRefByRunId = useRef<Map<string, Suite>>(new Map());
  /** 历史行用 ref 引用，避免 mounted-effect 把它纳入依赖造成不必要重建。 */
  const historyRef = useRef<LogLine[] | undefined>(historyLines);
  historyRef.current = historyLines;

  const [snapshots, setSnapshots] = useState<SuiteSnapshot[]>([]);
  const hitsByRunRef = useRef<Map<string, Hit[]>>(new Map());
  const [nextLineId, setNextLineId] = useState(0);

  // serial 变化：换一个全新的 RunnerSet，避免跨设备 lineId 撞车
  useEffect(() => {
    runnerSetRef.current = new RunnerSet();
    hitsByRunRef.current = new Map();
    suiteRefByRunId.current = new Map();
    setSnapshots([]);
    setNextLineId(0);
  }, [serial]);

  // 同步 mounted -> RunnerSet：add / remove / pause / resume / 替换（suite 引用变化）
  useEffect(() => {
    const current = runnerSetRef.current!;
    const fallback = (nowMs ?? Date.now)();
    const replay = (runId: string): void => {
      const hist = historyRef.current;
      if (!hist || hist.length === 0) return;
      const runner = current.list().find((r) => r.getRunId() === runId);
      if (!runner) return;
      // 直接喂到 runner（不走 RunnerSet.feed 广播），避免被其它 runner 重复消费。
      // history 里的 LogLine 已经在 useLogcatStream 里编号过，使用同一 lineId
      // 后 SPA 行内 marker 通过 hitsAt 仍能反查命中。
      for (const line of hist) {
        try {
          runner.feed(convertLogLine(line, fallback) as never);
        } catch {
          /* skip 单行错误 */
        }
      }
    };

    for (const m of mounted) {
      const knownSuite = suiteRefByRunId.current.get(m.runId);
      if (!current.has(m.runId)) {
        try {
          current.add({ runId: m.runId, suite: m.suite, params: m.params });
          suiteRefByRunId.current.set(m.runId, m.suite);
          hitsByRunRef.current.set(m.runId, []);
          replay(m.runId);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[checkpoint] add suite failed', m.suite.id, e);
        }
      } else if (knownSuite && knownSuite !== m.suite) {
        // YAML 热更：先 remove 老 runner，再用新 suite 重建并回放历史。
        current.remove(m.runId);
        hitsByRunRef.current.set(m.runId, []);
        try {
          current.add({ runId: m.runId, suite: m.suite, params: m.params });
          suiteRefByRunId.current.set(m.runId, m.suite);
          replay(m.runId);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[checkpoint] re-add suite failed', m.suite.id, e);
          suiteRefByRunId.current.delete(m.runId);
        }
      }
      if (m.active) current.resume(m.runId);
      else current.pause(m.runId);
    }
    // 移除已不在 mounted 列表的 runner
    const wantedIds = new Set(mounted.map((m) => m.runId));
    for (const r of current.list()) {
      if (!wantedIds.has(r.getRunId())) {
        current.remove(r.getRunId());
        hitsByRunRef.current.delete(r.getRunId());
        suiteRefByRunId.current.delete(r.getRunId());
      }
    }
    setSnapshots(current.snapshot());
  }, [mounted, nowMs]);

  // 注册事件 listener
  useEffect(() => {
    const current = runnerSetRef.current!;
    let pending = false;
    const flush = (): void => {
      if (pending) return;
      pending = true;
      setTimeout(() => {
        pending = false;
        setSnapshots(current.snapshot());
        setNextLineId(current.getNextLineId());
      }, 100);
    };
    const off = current.on((ev: SuiteEvent) => {
      if (ev.type === 'hit') {
        const arr = hitsByRunRef.current.get(ev.runId);
        if (arr) arr.push(ev.hit);
      }
      flush();
    });
    return () => off();
  }, [serial]);

  // chunk -> feed
  useLogcatChunks(serial, (chunk: LogLine[], cleared: boolean) => {
    const current = runnerSetRef.current!;
    if (cleared) {
      // 清空：重建 RunnerSet 但保留挂载列表
      const fresh = new RunnerSet();
      const freshSuiteRefs = new Map<string, Suite>();
      for (const m of mounted) {
        try {
          fresh.add({ runId: m.runId, suite: m.suite, params: m.params });
          freshSuiteRefs.set(m.runId, m.suite);
          hitsByRunRef.current.set(m.runId, []);
        } catch {
          /* skip */
        }
        if (!m.active) fresh.pause(m.runId);
      }
      runnerSetRef.current = fresh;
      suiteRefByRunId.current = freshSuiteRefs;
      setSnapshots(fresh.snapshot());
      setNextLineId(0);
      return;
    }
    if (chunk.length === 0) return;
    const fallback = (nowMs ?? Date.now)();
    for (const line of chunk) {
      current.feed(convertLogLine(line, fallback));
    }
    setNextLineId(current.getNextLineId());
  });

  return useMemo<RunnerSetController>(() => {
    const current = runnerSetRef.current!;
    return {
      snapshots,
      hitsAtFn: (lineId: number) => current.hitsAt(lineId),
      nextLineId,
      finishAll: () => current.finishAll(),
      runnerSet: current,
      hitsByRun: hitsByRunRef.current,
    };
  }, [snapshots, nextLineId]);
}
